import {
  DocumentData,
  DocumentReference,
  DocumentSnapshot,
  FieldValue,
} from "@atgof-firebase/firebase";
import { TextStyle } from "react-native";
import { Editable } from "./Editable";
import { UserInkColour } from "../common/membership";
import { useDocument } from "../data/useDocument";
import { useProject } from "../data/useProject";
import { useTheme } from "react-native-paper";
import { UserContext } from "../data/userContext";
import React from "react";
import { sheetsCollection } from "@atgof-common/sheet";

export type EditableAttrProps = {
  attr: string;
  readonly?: boolean;
  isMulti?: boolean;
  refOrSnapshot: DocumentReference | DocumentSnapshot | undefined;
  inkColours: UserInkColour[];
  createIfNeeded?: {
    ref: DocumentReference | undefined;
    data: DocumentData;
  };
};

export function EditableAttr({
  attr,
  readonly,
  isMulti,
  refOrSnapshot,
  inkColours,
  createIfNeeded,
}: EditableAttrProps) {
  const project = useProject();
  const { dark, colors } = useTheme();
  const { user } = React.useContext(UserContext);
  const snapshot = useDocument(refOrSnapshot);
  const value = snapshot?.get(attr);
  const lastModifiedByUserId: string | undefined = snapshot?.get(
    `latestModifications.${attr}.u`,
  );
  const inkColour = inkColours.find(
    ({ userId }) => userId === lastModifiedByUserId,
  )?.inkColour;
  const ref = React.useMemo(
    () =>
      snapshot?.ref ?? (project ? sheetsCollection(project).doc() : undefined),
    [snapshot?.ref, project],
  );
  const exists = snapshot !== undefined;
  const onChange = React.useCallback(
    (newValue: string[] | string) => {
      if (value === newValue || (!exists && newValue.length == 0)) return;
      const modFields = {
        lastModifiedBy: user.ref,
        lastModifiedAt: FieldValue.serverTimestamp(),
        [attr]: newValue,
      };
      if (exists) ref?.update(modFields);
      else
        createIfNeeded?.ref?.set({
          ...createIfNeeded.data,
          ...modFields,
        });
    },
    [value, ref?.path, exists, user, createIfNeeded],
  );
  const style: TextStyle = {
    alignSelf: "flex-start",
    color: inkColour
      ? dark
        ? inkColour.dark
        : inkColour.light
      : colors.onBackground,
  };
  if (isMulti) {
    // TODO This could do with more robust support
    const values: string[] = value ?? [""];
    return values.map((v, i) => (
      <Editable
        key={i}
        style={style}
        readonly={readonly}
        text={v}
        onChange={(newValue) => {
          let values_ = [...values];
          values_[i] = newValue;
          onChange(values_);
        }}
      />
    ));
  }
  return (
    <Editable
      style={style}
      readonly={readonly}
      text={value ?? ""}
      onChange={onChange}
    />
  );
}
